import { useState, useEffect } from 'react';
import { C } from '../styles/colours.js';
import socket from '../socket.js';
import { LANGUAGES, T } from '../translations.js';
import { getText } from '../questionBank.js';

function Player({ onBack, lang = 'en' }) {
  const urlParams = new URLSearchParams(window.location.search);
  const roomFromURL = urlParams.get('room') || '';

  const [playerName, setPlayerName] = useState('');
  const [roomCode, setRoomCode] = useState(roomFromURL);
  const [joined, setJoined] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [phase, setPhase] = useState('waiting');
  const [buzzed, setBuzzed] = useState(false);
  const [lockedBy, setLockedBy] = useState(null);
  const [pts, setPts] = useState(0);
  const [qIndex, setQIndex] = useState(0);
  const [result, setResult] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [winner, setWinner] = useState(null);
  const [roomFull, setRoomFull] = useState(false);
  const [iWasCorrect, setIWasCorrect] = useState(false);
  const [buzzOrder, setBuzzOrder] = useState([]);

  const t = T[lang] || T.en;
  const isRTL = lang === 'ar';
  const currentLang = LANGUAGES.find(l => l.code === lang);

  useEffect(() => {
    socket.on('game-started', ({ qIndex: startIndex, questions: qs }) => {
      setGameStarted(true);
      if (startIndex !== undefined) setQIndex(startIndex);
      if (qs) setQuestions(qs);
    });

    socket.on('buzzers-open', () => {
      setPhase('open');
      setBuzzed(false);
      setLockedBy(null);
      setResult(null);
      setIWasCorrect(false);
      setBuzzOrder([]);
    });

    socket.on('first-buzz', ({ name, buzzOrder: order }) => {
      if (order) setBuzzOrder(order);
      if (name !== playerName) {
        setLockedBy(name);
        setPhase('locked');
      }
    });

    socket.on('buzz-update', (order) => {
      if (order) setBuzzOrder(order);
    });

    socket.on('answer-wrong', () => {
      setBuzzed(false);
      setPhase('waiting');
    });

    socket.on('answer-correct', ({ players, playerName: correctPlayer }) => {
      const me = players.find(p => p.name === playerName);
      if (me) setPts(me.pts);
      setResult('correct');
      setPhase('waiting');
      setBuzzed(false);
      setIWasCorrect(correctPlayer === playerName);
    });

    socket.on('next-in-line', ({ name }) => {
      if (name === playerName) {
        setBuzzed(true);
        setLockedBy(null);
        setPhase('open');
      }
    });

    socket.on('new-question', (newIndex) => {
      setQIndex(newIndex);
      setPhase('waiting');
      setBuzzed(false);
      setLockedBy(null);
      setResult(null);
      setIWasCorrect(false);
      setBuzzOrder([]);
    });

    socket.on('winner-revealed', (winnerData) => {
      setWinner(winnerData);
    });

    socket.on('room-full', () => {
      setRoomFull(true);
    });

    return () => {
      socket.off('game-started');
      socket.off('buzzers-open');
      socket.off('first-buzz');
      socket.off('buzz-update');
      socket.off('answer-wrong');
      socket.off('answer-correct');
      socket.off('next-in-line');
      socket.off('new-question');
      socket.off('winner-revealed');
      socket.off('room-full');
    };
  }, [playerName]);

  const joinRoom = () => {
    if (!playerName.trim() || !roomCode.trim()) return;
    socket.emit('join-room', { roomCode: roomCode.toUpperCase(), playerName });
    setJoined(true);
  };

  const handleBuzz = () => {
    if (buzzed || phase !== 'open') return;
    setBuzzed(true);
    socket.emit('buzz', { roomCode: roomCode.toUpperCase(), playerName });
  };

  if (!joined) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} style={{ background: C.bg, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, padding: '20px', maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 16 }}>
        <button onClick={onBack} style={{ background: C.card, border: 'none', color: C.textSub, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, alignSelf: isRTL ? 'flex-end' : 'flex-start' }}>
          ← Back {currentLang?.flag}
        </button>

        <div style={{ textAlign: 'center', marginBottom: 10 }}>
          <div style={{ width: 70, height: 70, borderRadius: 16, background: 'linear-gradient(145deg, #e8526a, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 28, margin: '0 auto 12px', color: C.white }}>TQ</div>
          <div style={{ fontSize: 24, fontWeight: 900 }}>{t.joinGame}</div>
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 6 }}>{t.yourName}</div>
          <input type="text" placeholder="..." value={playerName} onChange={e => setPlayerName(e.target.value)}
            style={{ width: '100%', background: C.inputBg, border: `1px solid ${C.inputBorder}`, borderRadius: 10, padding: '12px 14px', fontSize: 14, color: C.white, outline: 'none', fontFamily: 'sans-serif', boxSizing: 'border-box', direction: isRTL ? 'rtl' : 'ltr' }} />
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 6 }}>{t.roomCode}</div>
          <input type="text" placeholder="e.g. 7F3K" value={roomCode} onChange={e => setRoomCode(e.target.value.toUpperCase())} maxLength={4}
            style={{ width: '100%', background: C.inputBg, border: `1px solid ${C.inputBorder}`, borderRadius: 10, padding: '12px 14px', fontSize: 22, fontWeight: 900, color: C.gold, outline: 'none', fontFamily: 'sans-serif', letterSpacing: 8, boxSizing: 'border-box' }} />
        </div>

        {roomFull && (
          <div style={{ background: `${C.red}20`, border: `1px solid ${C.red}40`, borderRadius: 12, padding: '12px 14px', textAlign: 'center', fontSize: 13, color: C.red, fontWeight: 700 }}>
            {t.roomFull}
          </div>
        )}

        <button onClick={joinRoom} disabled={!playerName.trim() || !roomCode.trim()}
          style={{ width: '100%', background: playerName.trim() && roomCode.trim() ? C.red : 'rgba(255,255,255,0.1)', color: playerName.trim() && roomCode.trim() ? C.white : C.textMuted, border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
          {t.joinBtn}
        </button>
      </div>
    );
  }

  if (!gameStarted) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} style={{ background: C.bg, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, padding: '20px', maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <div style={{ fontSize: 56 }}>⏳</div>
        <div style={{ fontSize: 24, fontWeight: 900 }}>You're in!</div>
        <div style={{ fontSize: 13, color: C.textSub, textAlign: 'center', lineHeight: 1.6 }}>{t.waitingHost}</div>
        <div style={{ background: `${C.gold}15`, border: `1px solid ${C.gold}30`, borderRadius: 14, padding: '14px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: C.textMuted, letterSpacing: 2, marginBottom: 4 }}>{t.youJoinedAs}</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.gold }}>{playerName}</div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>{t.room}: {roomCode}</div>
        </div>
      </div>
    );
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ background: C.bg, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, padding: '20px', maxWidth: 480, margin: '0 auto' }}>

      {winner && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(7,7,26,0.97)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 20 }}>
          <div style={{ fontSize: 80, marginBottom: 16 }}>🏆</div>
          <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 3, color: C.gold, marginBottom: 8 }}>{t.winner}</div>
          <div style={{ fontSize: 42, fontWeight: 900, color: C.white, marginBottom: 8, textAlign: 'center' }}>{winner.name}</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: C.gold, marginBottom: 24 }}>{winner.pts} pts</div>
          {winner.name === playerName && (
            <div style={{ background: `${C.gold}20`, border: `1px solid ${C.gold}40`, borderRadius: 14, padding: '12px 24px', fontSize: 16, fontWeight: 700, color: C.gold, marginBottom: 24 }}>
              🎉 {t.thatsYou}
            </div>
          )}
          <button onClick={onBack} style={{ width: '100%', maxWidth: 300, background: C.red, color: C.white, border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 800, cursor: 'pointer', marginBottom: 12 }}>
            {t.backHome}
          </button>
          <button onClick={() => window.location.reload()} style={{ width: '100%', maxWidth: 300, background: 'rgba(255,255,255,0.08)', color: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
            {t.playAgain}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.textSub }}>👤 {playerName}</div>
        <div style={{ fontWeight: 900, fontSize: 24, color: C.gold }}>{pts} pts</div>
      </div>

      <div style={{ background: 'rgba(255,209,102,0.08)', border: `2px solid ${C.gold}40`, borderRadius: 14, padding: '20px 16px', textAlign: 'center', marginBottom: 28 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.gold, marginBottom: 8 }}>
          {t.question} {qIndex + 1} {t.of} {questions.length}
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.6, color: C.white }}>{getText(questions[qIndex]?.q, lang) || '⏳'}</div>
      </div>

      {phase === 'waiting' && result !== 'correct' && (
        <div style={{ background: C.card, border: `2px solid ${C.border}`, borderRadius: 22, padding: '44px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 10, opacity: 0.4 }}>🔒</div>
          <div style={{ fontWeight: 900, fontSize: 26, color: C.textSub }}>{t.wait}</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>{t.listenQuestion}</div>
        </div>
      )}

      {phase === 'open' && !buzzed && (
        <button onClick={handleBuzz} style={{ width: '100%', background: 'linear-gradient(160deg, #EF476F, #b52d50)', border: 'none', borderRadius: 22, padding: '44px 20px', cursor: 'pointer', boxShadow: '0 16px 50px rgba(239,71,111,0.5)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 48 }}>🔔</div>
          <div style={{ fontSize: 40, fontWeight: 900, letterSpacing: 4, color: C.white }}>{t.buzzIn}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', letterSpacing: 1 }}>{t.pressFirst}</div>
        </button>
      )}

      {buzzed && !result && (
        <div style={{ background: 'linear-gradient(160deg, #06D6A0, #04a07a)', borderRadius: 22, padding: '44px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: '#000' }}>{t.youBuzzed}</div>
          <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)', marginTop: 8 }}>{t.answerOutLoud}</div>
        </div>
      )}

      {phase === 'locked' && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 22, padding: '44px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, opacity: 0.4, marginBottom: 8 }}>⏸️</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.textSub }}>{t.locked}</div>
          <div style={{ fontSize: 12, color: C.red, marginTop: 6, fontWeight: 700 }}>{lockedBy} {t.buzzedFirst}</div>
        </div>
      )}

      {result === 'correct' && iWasCorrect && (
        <>
          <div style={{ background: 'linear-gradient(160deg, #06D6A0, #04a07a)', borderRadius: 22, padding: '30px 20px', textAlign: 'center', marginTop: 16 }}>
            <div style={{ fontSize: 40 }}>🎉</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#000', marginTop: 8 }}>{t.wellDone}</div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <div style={{ fontSize: 13, color: C.textSub }}>{t.nextQuestion}</div>
          </div>
        </>
      )}

      {result === 'correct' && !iWasCorrect && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <div style={{ fontSize: 13, color: C.textSub }}>{t.nextQuestion}</div>
        </div>
      )}

      {buzzOrder.length > 0 && phase !== 'waiting' && (
        <div style={{ marginTop: 16, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '12px 14px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: C.textMuted, marginBottom: 8 }}>{t.buzzOrderLabel}</div>
          {buzzOrder.slice(0, 3).map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, marginBottom: i < 2 ? 6 : 0, color: b.name === playerName ? C.gold : C.white }}>
              <span style={{ fontSize: 18 }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>
              <span>{b.name}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20, fontSize: 11, color: C.textMuted }}>
        <span>{t.room}: {roomCode}</span>
        <span>Q{qIndex + 1} / {questions.length}</span>
      </div>
    </div>
  );
}

export default Player;